<template>
  <div
    class="modern-container"
    aria-label="Liste des demandes de nouvelle livraison"
    role="main"
  >
    <div class="tickets-header">
      <div class="header-content">
        <h1 class="simple-title">
          Demandes de nouvelle livraison
        </h1>
      </div>
    </div>
    <div class="modern-card">
      <div class="card-header">
        <div
          class="stats-row"
          aria-label="Statistiques des demandes de relivraison"
        >
          <div class="stat-item">
            <span
              class="stat-number"
              aria-live="polite"
            >{{ pagination.count || requests.length }}</span>
            <span class="stat-label">Total</span>
          </div>
          <div class="stat-item">
            <span
              class="stat-number"
              aria-live="polite"
            >{{ getPendingCount() }}</span>
            <span class="stat-label">En attente</span>
          </div>
          <div class="stat-item">
            <span
              class="stat-number"
              aria-live="polite"
            >{{ getAcceptedCount() }}</span>
            <span class="stat-label">Acceptées</span>
          </div>
          <div class="stat-item">
            <span
              class="stat-number"
              aria-live="polite"
            >{{ getRejectedCount() }}</span>
            <span class="stat-label">Rejetées</span>
          </div>
        </div>
        <div
          class="filter-bar mt-4 mb-2 px-0 px-md-3 py-2 d-flex flex-wrap align-items-center justify-content-between"
          style="background: #f8f9fa; border-radius: 12px; box-shadow: 0 1px 4px rgba(0,0,0,0.04);"
        >
          <div class="d-flex flex-wrap align-items-center gap-3">
            <div class="filter-group d-flex align-items-center mr-3 mb-2 mb-md-0">
              <i class="fas fa-search text-muted mr-2" />
              <input
                v-model="search"
                type="text"
                class="form-control filter-input"
                placeholder="Rechercher commande, fournisseur..."
                aria-label="Recherche de relivraison"
                style="min-width: 220px; border-radius: 8px; border: 1px solid #e2e8f0; box-shadow: none;"
                @keyup.enter="applyFilters"
              >
            </div>
            <div class="filter-group d-flex align-items-center mr-3 mb-2 mb-md-0">
              <i class="fas fa-filter text-muted mr-2" />
              <select
                v-model="statusFilter"
                class="form-control filter-select"
                aria-label="Filtrer par statut"
                style="min-width: 170px; border-radius: 8px; border: 1px solid #e2e8f0; box-shadow: none;"
                @change="applyFilters"
              >
                <option value="">
                  Tous les statuts
                </option>
                <option value="pending">
                  En attente
                </option>
                <option value="accepted">
                  Acceptée
                </option>
                <option value="rejected">
                  Rejetée
                </option>
              </select>
            </div>
            <div class="filter-group d-flex align-items-center mr-3 mb-2 mb-md-0">
              <i class="fas fa-calendar text-muted mr-2" />
              <select
                v-model="dateRangeFilter"
                class="form-control filter-select"
                aria-label="Filtrer par période"
                style="min-width: 170px; border-radius: 8px; border: 1px solid #e2e8f0; box-shadow: none;"
                @change="applyFilters"
              >
                <option value="">
                  Toutes les dates
                </option>
                <option value="today">
                  Aujourd'hui
                </option>
                <option value="yesterday">
                  Hier
                </option>
                <option value="this_week">
                  Cette semaine
                </option>
                <option value="last_week">
                  Semaine dernière
                </option>
                <option value="this_month">
                  Ce mois
                </option>
                <option value="last_month">
                  Mois dernier
                </option>
                <option value="last_7_days">
                  7 derniers jours
                </option>
                <option value="last_30_days">
                  30 derniers jours
                </option>
              </select>
            </div>
            <button
              class="btn btn-outline-secondary ml-2"
              aria-label="Réinitialiser les filtres"
              style="border-radius: 8px;"
              @click="resetFilters"
            >
              <i class="fas fa-times mr-1" /> Réinitialiser
            </button>
          </div>
        </div>
        <div
          v-if="search || statusFilter || dateRangeFilter"
          class="applied-filters mt-2 d-flex flex-wrap align-items-center gap-2"
        >
          <span
            v-if="search"
            class="applied-filter-chip"
          >
            <i class="fas fa-search mr-1" />
            <span>{{ search }}</span>
            <button
              class="clear-chip-btn ml-1"
              aria-label="Effacer la recherche"
              @click="search = ''; applyFilters()"
            >&times;</button>
          </span>
          <span
            v-if="statusFilter"
            class="applied-filter-chip"
          >
            <i class="fas fa-filter mr-1" />
            <span>{{ getStatusLabel(statusFilter) }}</span>
            <button
              class="clear-chip-btn ml-1"
              aria-label="Effacer le filtre de statut"
              @click="statusFilter = ''; applyFilters()"
            >&times;</button>
          </span>
          <span
            v-if="dateRangeFilter"
            class="applied-filter-chip"
          >
            <i class="fas fa-calendar mr-1" />
            <span>{{ getDateRangeLabel(dateRangeFilter) }}</span>
            <button
              class="clear-chip-btn ml-1"
              aria-label="Effacer le filtre de date"
              @click="dateRangeFilter = ''; applyFilters()"
            >&times;</button>
          </span>
          <span
            v-if="search || statusFilter || dateRangeFilter"
            class="ml-2"
          >
            <button
              class="btn btn-link p-0 applied-filters-clear-all"
              aria-label="Effacer tous les filtres"
              @click="resetFilters"
            >
              <i class="fas fa-times-circle mr-1" /> Effacer tous les filtres
            </button>
          </span>
        </div>
      </div>
      <div class="modern-table-container">
        <template v-if="loading">
          <div class="loading-state">
            <div class="loading-spinner">
              <i class="fas fa-spinner fa-spin" />
            </div>
            <span>Chargement des demandes...</span>
          </div>
        </template>
        <template v-else-if="requests.length === 0">
          <div class="text-center py-5">
            <div
              class="no-results-card shadow-sm enhanced-empty-state"
              style="background: #fff; border-radius: 20px; padding: 2.5rem 2rem; max-width: 520px; margin: 0 auto; box-shadow: 0 8px 32px rgba(156,163,175,0.10);"
            >
              <div class="mb-4">
                <img
                  src="/public/img/icons/empty-support.svg"
                  alt="Aucune demande"
                  style="height: 110px; opacity: 0.85;"
                  onerror="this.style.display='none'"
                >
                <i
                  v-if="false"
                  class="fas fa-inbox fa-4x text-muted"
                />
              </div>
              <h3
                class="text-muted mb-2 font-weight-bold"
                style="font-size: 1.5rem;"
              >
                Aucune demande trouvée
              </h3>
              <p
                class="text-muted mb-4"
                style="font-size: 1.08rem;"
              >
                <span v-if="search || statusFilter || dateRangeFilter">
                  <span style="font-weight: 500;">Aucune demande ne correspond à vos critères.</span><br>
                  Essayez d'élargir votre recherche ou de réinitialiser les filtres pour voir plus de résultats.
                </span>
                <span v-else>
                  <span style="font-weight: 500;">Il n'y a actuellement aucune demande de relivraison.</span><br>
                  Les demandes de relivraison apparaîtront ici lorsqu'elles seront créées.
                </span>
              </p>
              <div v-if="search || statusFilter || dateRangeFilter">
                <b-button
                  variant="outline-primary"
                  size="lg"
                  class="mt-1 px-4"
                  @click="resetFilters"
                >
                  <i class="fas fa-sync-alt mr-1" /> Réinitialiser les filtres
                </b-button>
              </div>
            </div>
          </div>
        </template>
        <template v-else>
          <b-table
            :items="requests"
            :fields="tableFields"
            responsive="sm"
            class="modern-table redelivery-table"
            hover
            borderless
            aria-label="Tableau des demandes de nouvelle livraison"
            caption-top
            @row-clicked="openQuickView"
          >
            <template #cell(order)="{ item }">
              <div class="order-cell">
                <span
                  v-b-tooltip.hover
                  :title="'Référence: #' + (item.order && item.order.id ? item.order.id : item.order)"
                  class="order-badge"
                  style="cursor:pointer"
                  @click.stop="copyOrderId(item.order && item.order.id)"
                >
                  #{{ (item.order && item.order.id) ? item.order.id : item.order }}
                </span>
                <span
                  v-if="item.order && item.order.name"
                  class="order-info ml-2"
                >{{ item.order.name }}</span>
                <span
                  v-else
                  class="order-info ml-2"
                >-</span>
              </div>
            </template>
            <template #cell(supplier)="{ item }">
              <div class="user-cell">
                <b-avatar
                  v-if="item.supplier && item.supplier.avatar"
                  :src="item.supplier.avatar"
                  size="32"
                  class="mr-2"
                />
                <div
                  v-else
                  class="user-avatar-alt mr-2"
                >
                  {{ getInitials(item.supplier && (item.supplier.full_name || item.supplier.username)) }}
                </div>
                <span
                  v-b-tooltip.hover
                  class="user-name"
                  :title="item.supplier && item.supplier.email ? item.supplier.email : ''"
                >
                  {{ (item.supplier && (item.supplier.full_name || item.supplier.username)) ? (item.supplier.full_name || item.supplier.username) : '-' }}
                </span>
              </div>
            </template>
            <template #cell(status)="{ item }">
              <span :class="['status-pill', getStatusClass(item.status)]">
                <i :class="getStatusIcon(item.status)" />
                {{ getStatusLabel(item.status) }}
              </span>
            </template>
            <template #cell(created_at)="{ item }">
              <div class="date-cell">
                <span class="date-text">{{ formatDate(item.created_at) }}</span>
              </div>
            </template>
            <template #cell(actions)="{ item }">
              <div class="actions-cell">
                <b-button
                  v-if="item.status && item.status.toLowerCase() === 'pending' && canManageRequests"
                  variant="success"
                  size="sm"
                  class="mr-2"
                  @click.stop="showAcceptModal(item)"
                >
                  <i class="fas fa-check mr-1" /> Accepter
                </b-button>
                <b-button
                  v-if="item.status && item.status.toLowerCase() === 'pending' && canManageRequests"
                  variant="danger"
                  size="sm"
                  @click.stop="showRejectModal(item)"
                >
                  <i class="fas fa-times mr-1" /> Rejeter
                </b-button>
                <span v-if="!item.status || item.status.toLowerCase() !== 'pending' || !canManageRequests">-</span>
              </div>
            </template>
          </b-table>

          <!-- Pagination Controls -->
          <div
            v-if="pagination.count > pagination.page_size"
            class="pagination-container d-flex justify-content-between align-items-center mt-4 px-3"
          >
            <div class="pagination-info">
              <span class="text-muted">
                Affichage de {{ getDisplayRange() }} sur {{ pagination.count }} demandes
              </span>
            </div>
            <b-pagination
              v-model="currentPage"
              :total-rows="pagination.count"
              :per-page="pagination.page_size"
              :limit="5"
              size="md"
              class="mb-0"
              @change="onPageChange"
            />
          </div>
        </template>
      </div>
    </div>
    <redelivery-request-quick-view
      :visible="showQuickView"
      :request="quickViewRequest"
      @close="closeQuickView"
    />
    <b-modal
      id="confirm-modal"
      v-model="showModal"
      :title="modalTitle"
      ok-title="Confirmer"
      cancel-title="Annuler"
      centered
      @ok="confirmAction"
    >
      {{ modalMessage }}
      <b-form-textarea
        v-if="modalType === 'reject'"
        v-model="adminResponse"
        placeholder="Motif du refus (optionnel)"
        rows="2"
        class="mt-2"
      />
    </b-modal>
  </div>
</template>

<script>

import axios from '@/libs/axios'
import {
  BButton, BTable, BModal, BFormTextarea, BPagination, BAvatar,
} from 'bootstrap-vue'
import storeAuth from '@/store/store'
import RedeliveryRequestQuickView from './RedeliveryRequestQuickView.vue'

export default {
  name: 'RedeliveryRequestList',
  components: {
    BButton,
    BTable,
    BAvatar,
    BModal,
    BFormTextarea,
    BPagination,
    RedeliveryRequestQuickView,
  },
  data() {
    return {
      requests: [],
      loading: false,
      fields: [
        { key: 'id', label: 'ID', sortable: true },
        { key: 'order', label: 'Commande', sortable: false },
        { key: 'supplier', label: 'Fournisseur', sortable: false },
        { key: 'status', label: 'Statut', sortable: true },
        { key: 'created_at', label: 'Demandée le', sortable: true },
        { key: 'actions', label: 'Actions' },
      ],
      user: {},
      search: '',
      statusFilter: '',
      dateRangeFilter: '',
      statusOptions: [
        { value: '', text: 'Tous les statuts' },
        { value: 'pending', text: 'En attente' },
        { value: 'accepted', text: 'Acceptée' },
        { value: 'rejected', text: 'Rejetée' },
      ],
      showModal: false,
      modalType: '',
      modalTitle: '',
      modalMessage: '',
      modalRequest: null,
      adminResponse: '',
      showQuickView: false,
      quickViewRequest: {},
      currentPage: 1,
      pagination: {
        count: 0,
        next: null,
        previous: null,
        page_size: 20,
        current_page: 1,
        total_pages: 1,
      },
    }
  },
  computed: {
    canManageRequests() {
      const userRole = storeAuth.getters.userRole || storeAuth.state.user?.role
      return ['admin', 'manager', 'warehouse_operator', 'customer_support'].includes(userRole)
    },
    tableFields() {
      // derive fields to avoid referencing computeds inside data()
      return this.fields.map(f => {
        if (f.key !== 'actions') return f
        return {
          ...f,
          thClass: this.canManageRequests ? '' : 'd-none',
          tdClass: this.canManageRequests ? '' : 'd-none',
        }
      })
    },
  },
  created() {
    this.user = storeAuth.state.user
    this.applyFilters()
  },
  methods: {
    openQuickView(request) {
      this.quickViewRequest = request
      this.showQuickView = true
    },
    closeQuickView() {
      this.showQuickView = false
      this.quickViewRequest = {}
    },
    async fetchRequests(params = {}) {
      this.loading = true
      try {
        const queryParams = {
          page: this.currentPage,
          ...params,
        }
        if (this.search) queryParams.search = this.search
        if (this.statusFilter) queryParams.status = this.statusFilter
        if (this.dateRangeFilter) queryParams.date_range = this.dateRangeFilter

        const { data } = await axios.get('/api/support/orderredeliveryrequests/', { params: queryParams })
        this.requests = data.results || data
        this.pagination = {
          count: data.count || 0,
          next: data.next,
          previous: data.previous,
          page_size: data.page_size || 20,
          current_page: data.current_page || this.currentPage,
          total_pages: data.total_pages || 1,
        }
      } catch (error) {
        console.error('Error fetching requests:', error)
        this.$bvToast.toast('Erreur lors du chargement des demandes', { variant: 'danger' })
      } finally {
        this.loading = false
      }
    },
    onPageChange(page) {
      this.currentPage = page
      this.applyFilters()
    },
    getDisplayRange() {
      const start = (this.currentPage - 1) * this.pagination.page_size + 1
      const end = Math.min(this.currentPage * this.pagination.page_size, this.pagination.count)
      return `${start}-${end}`
    },
    applyFilters() {
      // Build params for backend filtering
      const params = {}
      if (this.statusFilter) params.status = this.statusFilter
      if (this.search) params.search = this.search
      if (this.dateRangeFilter) params.date_range = this.dateRangeFilter
      this.fetchRequests(params)
    },
    resetFilters() {
      this.statusFilter = ''
      this.search = ''
      this.dateRangeFilter = ''
      this.currentPage = 1
      this.applyFilters()
    },
    showAcceptModal(request) {
      this.modalType = 'accept'
      this.modalTitle = 'Accepter la demande de relivraison'
      this.modalMessage = `Confirmer l'acceptation de la demande pour la commande #${request.order && request.order.id ? request.order.id : request.order} ?`
      this.modalRequest = request
      this.adminResponse = ''
      this.showModal = true
    },
    showRejectModal(request) {
      this.modalType = 'reject'
      this.modalTitle = 'Rejeter la demande de relivraison'
      this.modalMessage = `Confirmer le rejet de la demande pour la commande #${request.order && request.order.id ? request.order.id : request.order} ?`
      this.modalRequest = request
      this.adminResponse = ''
      this.showModal = true
    },
    async confirmAction() {
      if (!this.modalRequest) return
      const { id } = this.modalRequest
      try {
        if (this.modalType === 'accept') {
          await axios.patch(`/api/support/orderredeliveryrequests/${id}/`, { action: 'accept', admin_response: this.adminResponse })
          this.$bvToast.toast('Demande acceptée avec succès.', { variant: 'success', autoHideDelay: 2500 })
        } else if (this.modalType === 'reject') {
          await axios.patch(`/api/support/orderredeliveryrequests/${id}/`, { action: 'reject', admin_response: this.adminResponse })
          this.$bvToast.toast('Demande rejetée.', { variant: 'danger', autoHideDelay: 2500 })
        }
        this.applyFilters() // Use applyFilters instead of fetchRequests to maintain current page
      } catch (e) {
        this.$bvToast.toast('Erreur lors du traitement.', { variant: 'danger', autoHideDelay: 2500 })
      }
      this.showModal = false
    },
    applySearch() {
      // Triggers computed property
    },
    copyOrderId(orderId) {
      if (!orderId) return
      navigator.clipboard.writeText(orderId.toString()).then(() => {
        this.$bvToast.toast(`Référence ${orderId} copiée!`, { variant: 'success', autoHideDelay: 2000 })
      })
    },
    getInitials(name) {
      if (!name) return '-'
      const parts = name.split(' ')
      if (parts.length === 1) return parts[0].charAt(0).toUpperCase()
      return parts[0].charAt(0).toUpperCase() + parts[1].charAt(0).toUpperCase()
    },
    getPendingCount() {
      return this.requests.filter(r => r.status && r.status.toLowerCase() === 'pending').length
    },
    getAcceptedCount() {
      return this.requests.filter(r => r.status && r.status.toLowerCase() === 'accepted').length
    },
    getRejectedCount() {
      return this.requests.filter(r => r.status && r.status.toLowerCase() === 'rejected').length
    },
    getStatusClass(status) {
      if (status === 'PENDING') return 'status-open'
      if (status === 'ACCEPTED') return 'status-resolved'
      if (status === 'REJECTED') return 'status-closed'
      return 'status-open'
    },
    getStatusIcon(status) {
      if (status === 'PENDING') return 'fas fa-clock'
      if (status === 'ACCEPTED') return 'fas fa-check'
      if (status === 'REJECTED') return 'fas fa-times'
      return 'fas fa-clock'
    },
    getStatusLabel(status) {
      if (!status) return '-'
      const s = status.toLowerCase()
      if (s === 'pending') return 'En attente'
      if (s === 'accepted') return 'Acceptée'
      if (s === 'rejected') return 'Rejetée'
      return status
    },
    getDateRangeLabel(range) {
      switch (range) {
        case 'today': return "Aujourd'hui"
        case 'yesterday': return 'Hier'
        case 'this_week': return 'Cette semaine'
        case 'last_week': return 'Semaine dernière'
        case 'this_month': return 'Ce mois'
        case 'last_month': return 'Mois dernier'
        case 'last_7_days': return '7 derniers jours'
        case 'last_30_days': return '30 derniers jours'
        default: return 'Toutes les dates'
      }
    },
    formatDate(date) {
      if (!date) return '-'
      return new Date(date).toLocaleString('fr-FR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
  },
}
</script>

<style scoped>
/* Modern Container */
.modern-container {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  min-height: 100vh;
  padding: 2rem 1.5rem;
  font-family: 'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}

/* Header Styles */
.tickets-header {
  margin-bottom: 2rem;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: linear-gradient(135deg, #f8f9fa 0%, #fca5a5 50%, #9ca3af 100%);
  border-radius: 12px;
  padding: 1.75rem 2.5rem;
  box-shadow: 0 4px 15px rgba(156, 163, 175, 0.25);
  border: none;
  position: relative;
  overflow: hidden;
}

.header-content::before {
  content: "";
  position: absolute;
  top: -30px;
  right: -30px;
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background: rgba(252, 165, 165, 0.3);
  z-index: 0;
}

.header-content::after {
  content: "";
  position: absolute;
  bottom: -20px;
  left: 30%;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: rgba(156, 163, 175, 0.2);
  z-index: 0;
}

.simple-title {
  font-size: 1.75rem;
  font-weight: 600;
  margin: 0;
  color: #2d3748;
  position: relative;
  z-index: 1;
}

/* Modern Card */
.modern-card {
  background: white;
  border-radius: 20px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.05);
}

.card-header {
  background: linear-gradient(135deg, #f8f9fa 0%, #f1f5f9 100%);
  padding: 1.75rem 2.5rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  position: relative;
}

.card-header::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, #fca5a5 0%, #e53e3e 50%, #9ca3af 100%);
}

.stats-row {
  display: flex;
  gap: 2rem;
}

.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.stat-number {
  font-size: 1.75rem;
  font-weight: 700;
  color: #e53e3e;
}

.stat-label {
  font-size: 0.875rem;
  color: #64748b;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Modern Table */
.modern-table-container {
  padding: 0;
  overflow-x: auto;
}

.modern-table {
  margin: 0;
  border: none;
}

.modern-table >>> thead th {
  background: linear-gradient(to bottom, #f8f9fa 0%, #e2e8f0 100%);
  color: #1e293b;
  border-right: 1px solid rgba(203, 213, 225, 0.4);
  padding: 1rem 1.5rem;
  font-weight: 700;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  position: sticky;
  top: 0;
  z-index: 10;
  border-bottom: 2px solid #cbd5e1;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  text-align: center;
  vertical-align: middle;
  position: relative;
  overflow: visible;
}

.modern-table >>> thead th[aria-sort="ascending"]::after {
  content: "▲";
  display: inline-block;
  margin-left: 0.5rem;
  font-size: 0.75rem;
  color: #e53e3e;
}

.modern-table >>> thead th[aria-sort="descending"]::after {
  content: "▼";
  display: inline-block;
  margin-left: 0.5rem;
  font-size: 0.75rem;
  color: #e53e3e;
}

.modern-table >>> thead th:hover {
  background: linear-gradient(to bottom, #e2e8f0 0%, #cbd5e1 100%);
  cursor: pointer;
}

.modern-table >>> thead th:first-child {
  border-top-left-radius: 0;
  border-left: none;
}

.modern-table >>> thead th:last-child {
  border-top-right-radius: 0;
  border-right: none;
}

.modern-table >>> tbody td {
  padding: 1.25rem 1.5rem;
  border: none;
  border-bottom: 1px solid #f1f5f9;
  vertical-align: middle;
}

.modern-table >>> tbody tr {
  transition: all 0.2s ease;
}

.modern-table >>> tbody tr:hover {
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

/* Cell Styles */
.order-cell {
  display: flex;
  align-items: center;
}

.order-badge {
  background: linear-gradient(135deg, #f1f5f9 0%, #fca5a5 50%, #9ca3af 100%);
  color: #000000;
  padding: 0.5rem 1rem;
  border-radius: 25px;
  font-weight: 600;
  font-size: 0.875rem;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(156, 163, 175, 0.3);
}

.order-badge:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(156, 163, 175, 0.4);
}

.order-info {
  font-weight: 500;
  color: #1e293b;
  margin-left: 0.5rem;
}

.user-cell {
  display: flex;
  align-items: center;
}

.user-avatar-alt {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.875rem;
}

.user-name {
  font-weight: 500;
  color: #1e293b;
}

.status-pill {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
}

.status-open {
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  color: white;
}

.status-resolved {
  background: #2FDD6F;
  color: white;
}

.status-closed {
  background: linear-gradient(135deg, #1a202c 0%, #2d3748 100%);
  color: white;
}

.date-cell {
  color: #64748b;
  font-size: 0.875rem;
}

.actions-cell {
  display: flex;
  gap: 0.5rem;
  justify-content: center;
}

/* Applied Filters Chips */
.applied-filters {
  margin-top: 0.5rem;
  gap: 0.5rem;
}
.applied-filter-chip {
  display: inline-flex;
  align-items: center;
  background: #e2e8f0;
  color: #374151;
  border-radius: 16px;
  padding: 0.25rem 0.75rem;
  font-size: 0.95em;
  margin-right: 0.5rem;
  margin-bottom: 0.25rem;
}
.clear-chip-btn {
  background: none;
  border: none;
  color: #b91c1c;
  font-size: 1.1em;
  margin-left: 0.25rem;
  cursor: pointer;
  padding: 0 0.25rem;
  line-height: 1;
}
.applied-filters-clear-all {
  color: #b91c1c;
  font-size: 0.98em;
  margin-left: 0.5rem;
  text-decoration: underline;
}

/* Pagination Styles */
.pagination-container {
  border-top: 1px solid #f1f5f9;
  padding-top: 1rem;
  background: #f8f9fa;
  border-radius: 0 0 20px 20px;
}

.pagination-info {
  font-size: 0.875rem;
  color: #64748b;
  font-weight: 500;
}

.pagination >>> .page-link {
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  color: #64748b;
  font-weight: 500;
  margin: 0 2px;
}

.pagination >>> .page-item.active .page-link {
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  border-color: #dc2626;
  color: white;
}

.pagination >>> .page-link:hover {
  background-color: #f1f5f9;
  border-color: #cbd5e1;
  color: #1e293b;
}

/* Responsive */
@media (max-width: 768px) {
  .modern-container {
    padding: 1rem;
  }
  .header-content {
    flex-direction: column;
    gap: 1rem;
    text-align: center;
  }
  .modern-title {
    font-size: 2rem;
  }
  .stats-row {
    justify-content: center;
    gap: 1rem;
  }
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0,0,0,0);
  border: 0;
}
</style>
